// preloaded routes
import LoginPage from '@/pages/LoginPage'
import HomeView from '@/views/HomeView'
// import PlayerPage from '@/pages/PlayerPage'
import PlayerPage from '@/pages/PlayerPage'
import RouterPage from '@/pages/RouterPage'
import store from '@/store'

/*
 * meta fields:
 *   capture: boolean - whether to capture analytics for this route (default: true)
 *   component: string - name of the component for this route (for keep-alive caching)
 *   hasCount: boolean - whether to show a count badge on the menu item
 *   keepAlive: boolean - whether to keep the component alive (cached) when navigating away
 *   layout: string - name of the layout to use for this route
 *   requiresAuthentication: boolean - whether authentication is required to access this route (default: true)
 *   scrollPosition: object - the saved scroll position of the page when navigating away { x: number, y: number }
 *   scrollToTop: boolean - whether to scroll to top when navigating to this route
 *   sharable: boolean - whether the route can be shared with unauthenticated users
 *   tier: number - minimum access tier required to access the route (for authentication)
 */

// route components

// lazy loading
function loadView(view) {
  return () => import(/* webpackChunkName: "[request]" */ `@/views/${view}.vue`)
}
function loadPage(page) {
  return () => import(/* webpackChunkName: "[request]" */ `@/pages/${page}.vue`)
}

// redirection
const getOrgIndexRoute = (query) => ({
  ...store.state.menuStore.indexRoute,
  query
})

/*
// work in progress - org and  user custom "Home" routes
const getOrgHomeRoute = (query) => {
  const orgHomeMenuItem = store.state.menuStore.homeMenuItem
  const userHomeMenuItem = store.state.userStore.settings?.homeMenuItem
  const homeMenuItem = userHomeMenuItem || orgHomeMenuItem
  const homeMenuRoute = { name: homeMenuItem }
  return {
    ...homeMenuRoute,
    query
  }
}
*/

export const appRoutes = [
  {
    path: '/',
    name: 'index',
    redirect: (to) => getOrgIndexRoute(to.query)
  },

  {
    path: '/error',
    name: 'error',
    component: loadPage('ErrorPage'),
    props: (route) => ({
      code: route.query.code,
      message: route.query.message
    }),
    meta: {
      tier: 0,
      layout: 'ScreenLayout',
      requiresAuthentication: false
    }
  },
  {
    path: '/jitsi/:room',
    name: 'jitsi',
    component: loadPage('JitsiPage'),
    props: (route) => ({
      subject: route.query.subject,
      room: route.params.room,
      token: route.query.token
    }),
    meta: {
      tier: 0,
      layout: 'ScreenLayout',
      requiresAuthentication: false
    }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    props: (route) => ({
      force: !!route.query?.force,
      redirect: route.query?.redirect,
      options: route.query?.options
    }),
    meta: {
      tier: 0,
      layout: 'ScreenLayout',
      requiresAuthentication: false,
      capture: false
    }
  },
  {
    path: '/logout',
    name: 'logout',
    component: loadPage('LogoutPage'),
    meta: {
      tier: 0,
      layout: 'ScreenLayout',
      requiresAuthentication: false,
      capture: false
    }
  },
  {
    path: '/meet/:room',
    name: 'meet',
    component: loadPage('JitsiPage'),
    props: true,
    meta: {
      tier: 2,
      layout: 'ScreenLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/redirect',
    name: 'redirect',
    component: RouterPage,
    props: (route) => ({
      redirect: route.query.redirect
    }),
    meta: {
      tier: 0,
      layout: 'ScreenLayout',
      requiresAuthentication: false,
      capture: false
    }
  },
  {
    path: '/splash',
    name: 'splash',
    component: loadPage('SplashPage'),
    props: (route) => ({
      params: route.query
    }),
    meta: {
      tier: 0,
      layout: 'ScreenLayout',
      requiresAuthentication: false
    }
  },
  {
    path: '/susi',
    name: 'susi',
    component: loadPage('SusiPage'),
    props: (route) => ({
      redirect: route.query?.redirect
    }),
    meta: {
      tier: 0,
      layout: 'PageLayout',
      requiresAuthentication: false
    }
  }
]

export const pageRoutes = [
  {
    path: '/join',
    name: 'join',
    component: loadPage('JoinPage'),
    meta: {
      tier: 0,
      layout: 'PageLayout',
      requiresAuthentication: false
    }
  },
  {
    path: '/channel/:slug',
    name: 'channel',
    component: loadPage('ChannelPage'),
    props: true,
    meta: {
      tier: 0,
      component: 'ChannelPage',
      keepAlive: true,
      layout: 'PageLayout',
      requiresAuthentication: false,
      sharable: true
    }
  },
  {
    path: '/ichannel/:slug',
    name: 'ichannel',
    component: loadPage('ChannelPage'),
    props: true,
    meta: {
      tier: 2,
      component: 'ChannelPage',
      keepAlive: true,
      layout: 'PageLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/player/:id',
    name: 'player',
    component: PlayerPage,
    props: true,
    meta: {
      tier: 0,
      keepAlive: false,
      layout: 'PlayerLayout',
      requiresAuthentication: false,
      sharable: true
    }
  },
  {
    path: '/iplayer/:id',
    name: 'iplayer',
    component: PlayerPage,
    props: true,
    meta: {
      tier: 2,
      keepAlive: false,
      layout: 'PlayerLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/post/:id',
    name: 'post',
    component: loadPage('PostPage'),
    props: true,
    meta: {
      tier: 0,
      keepAlive: false,
      layout: 'PageLayout',
      requiresAuthentication: false,
      sharable: true
    }
  },
  {
    path: '/ipost/:id',
    name: 'ipost',
    component: loadPage('PostPage'),
    props: true,
    meta: {
      tier: 2,
      keepAlive: false,
      layout: 'PageLayout',
      requiresAuthentication: true
    }
  }
]

export const menuRoutes = [
  {
    path: '/about',
    name: 'about',
    component: loadView('AboutView'),
    meta: {
      tier: 0,
      keepAlive: false,
      layout: 'AppLayout',
      requiresAuthentication: false
    }
  },
  {
    path: '/favourites',
    name: 'favourites',
    component: loadView('FavouritesView'),
    meta: {
      tier: 2,
      component: 'FavouritesView',
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/channels',
    name: 'channels',
    component: loadView('ChannelsView'),
    meta: {
      tier: 1,
      component: 'ChannelsView',
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/clients',
    name: 'clients',
    component: loadView('ClientsView'),
    meta: {
      tier: 2,
      component: 'ClientsView',
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/collection/:keyword',
    name: 'collection',
    component: loadView('ContentView'),
    props: true,
    meta: {
      tier: 1,
      component: 'ContentView',
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/companies',
    name: 'companies',
    component: loadView('CompaniesView'),
    meta: {
      tier: 2,
      component: 'CompaniesView',
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/contacts',
    name: 'contacts',
    component: loadView('ContactsView'),
    meta: {
      tier: 2,
      component: 'ContactsView',
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: loadView('DashboardView'),
    meta: {
      tier: 2,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/help',
    name: 'help',
    component: loadView('HelpView'),
    meta: {
      tier: 0,
      keepAlive: false,
      layout: 'AppLayout',
      requiresAuthentication: false
    }
  },
  {
    path: '/home',
    name: 'home',
    component: HomeView,
    meta: {
      tier: 1,
      component: 'HomeView',
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/library',
    name: 'library',
    component: loadView('ContentView'),
    props: {
      keyword: 'library'
    },
    meta: {
      tier: 1,
      component: 'ContentView',
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/meeting',
    name: 'meeting',
    component: loadView('MeetingView'),
    meta: {
      tier: 2,
      keepAlive: false,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/faculty',
    name: 'persons',
    component: loadView('PersonsView'),
    meta: {
      tier: 1,
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/faculty',
    name: 'faculty',
    component: loadView('PersonsView'),
    meta: {
      tier: 1,
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/posts',
    name: 'posts',
    component: loadView('PostsView'),
    meta: {
      tier: 1,
      component: 'PostsView',
      hasCount: true,
      keepAlive: true,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/schedule',
    name: 'schedule',
    component: loadView('ScheduleView'),
    meta: {
      tier: 2,
      keepAlive: false,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/tools',
    name: 'tools',
    component: loadView('ToolsView'),
    meta: {
      tier: 1,
      keepAlive: false,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  },
  {
    path: '/web',
    name: 'web',
    component: loadView('WebView'),
    props: (route) => ({
      href: route.query.href
    }),
    meta: {
      tier: 2,
      keepAlive: false,
      layout: 'AppLayout',
      requiresAuthentication: true
    }
  }
]

export const errorRoutes = [
  {
    path: '/404',
    name: 'error404',
    component: loadPage('Error404Page'),
    meta: {
      tier: 0,
      keepAlive: false,
      layout: 'ScreenLayout',
      requiresAuthentication: false
    }
  },
  {
    path: '*',
    name: 'redirect404',
    redirect: { name: 'error404' },
    meta: {
      tier: 0,
      requiresAuthentication: false
    }
  }
]

// https://stackoverflow.com/questions/48515023/display-different-vuejs-components-for-mobile-browsers
// component: responsiveRoute({ // takes this input and returns the right answer
//   route level code-splitting
//   this generates a separate chunk (login-xs.[hash].js) for this route
//   which is lazy-loaded when the route is visited.
// xs: () => import(/* webpackChunkName: "login-xs" */ '@/views/Login/Login-xs.vue'),
//   sm key is missing, it falls back to xs
// md: () => import(/* webpackChunkName: "login-md" */ '@/views/Login/Login-md.vue')
//   lg, xl keys are missing falls back to md
// })
